import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useApi from '../../helpers/TerserahApi';
import useAlertStore from '../../hooks/useAlert';
import { timeAgo } from '../../helpers/TimeUtils';
import VerificationBadge from '../VerificationBadge';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Message,
  MessageInput,
  SendButton,
  CloseButton,
  SellerInfo,
  MessagesContainer,
  EmptyState,
  OfferButton,
  OfferModal,
  OfferCard,
  QuickReplyContainer,
  QuickReplyButton
} from './style';

// Quick reply templates
const QUICK_REPLIES = [
  { id: 1, text: '¿Está disponible?' },
  { id: 2, text: '¿Cuál es el precio final?' },
  { id: 3, text: '¿Acepta cambios?' },
  { id: 4, text: '¿Dónde está ubicado?' },
  { id: 5, text: '¿Puedo verlo primero?' },
  { id: 6, text: '¿Envío a domicilio?' }
];

const ChatModal = ({ isOpen, onClose, adId, sellerInfo, adTitle, adPrice, isPriceNegotiable, autoOpenOffer = false }) => {
  const { t } = useTranslation();
  const api = useApi();
  const { showAlert } = useAlertStore();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [offers, setOffers] = useState([]);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen && adId) {
      loadMessages();
      loadOffers();
      
      // Auto-open offer modal if requested
      if (autoOpenOffer) {
        setTimeout(() => setShowOfferModal(true), 300);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, adId, autoOpenOffer]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const json = await api.getConversation(adId);
      if (json && json.messages) {
        setMessages(json.messages);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
    }
    setLoading(false);
  };

  const loadOffers = async () => {
    try {
      const json = await api.getAdOffers(adId);
      if (json && json.offers) {
        setOffers(json.offers);
      }
    } catch (err) {
      console.error('Error loading offers:', err);
    }
  };

  const handleSendMessage = async (e, messageText = null) => {
    if (e) e.preventDefault();
    const textToSend = messageText || newMessage.trim();
    if (!textToSend || sending) return;

    setSending(true);
    try {
      const json = await api.sendMessage(adId, textToSend);
      if (json.success) {
        const tempMessage = {
          id: Date.now(),
          message: textToSend,
          from_me: true,
          created_at: new Date().toISOString()
        };
        setMessages([...messages, tempMessage]);
        setNewMessage('');
        setShowQuickReplies(false);
        
        setTimeout(() => loadMessages(), 500);
      } else {
        showAlert(json.error || t('error_sending_message'), 'Error', 'error');
      }
    } catch (err) {
      showAlert(t('error_sending_message'), 'Error', 'error');
    }
    setSending(false);
  };

  const handleQuickReply = (text) => {
    handleSendMessage(null, text);
  };

  const handleSendOffer = async () => {
    if (!offerAmount || parseFloat(offerAmount) <= 0) {
      showAlert('Por favor ingresa una oferta válida', 'Warning', 'warning');
      return;
    }

    // Validate minimum offer (75% of price)
    if (adPrice && parseFloat(offerAmount) < (adPrice * 0.75)) {
      showAlert(
        `La oferta debe ser al menos el 75% del precio (Rp ${(adPrice * 0.75).toLocaleString('id-ID')})`,
        'Warning',
        'warning'
      );
      return;
    }

    try {
      const json = await api.createOffer(adId, {
        amount: parseFloat(offerAmount),
        message: offerMessage
      });

      if (json.success) {
        showAlert('¡Oferta enviada con éxito!', 'Success', 'success');
        setShowOfferModal(false);
        setOfferAmount('');
        setOfferMessage('');
        loadOffers();
        
        // Send automatic message
        const offerText = t('offer_sent_message', { amount: parseFloat(offerAmount).toLocaleString('id-ID') }) || `He enviado una oferta de Rp ${parseFloat(offerAmount).toLocaleString('id-ID')}`;
        handleSendMessage(null, offerText);
      } else {
        showAlert(json.message || json.error || 'Error al enviar oferta', 'Error', 'error');
      }
    } catch (err) {
      showAlert('Error al enviar oferta', 'Error', 'error');
    }
  };

  const handleAcceptOffer = async (offerId) => {
    try {
      const json = await api.acceptOffer(offerId);
      if (json.success) {
        showAlert('¡Oferta aceptada!', 'Success', 'success');
        loadOffers();
      }
    } catch (err) {
      showAlert('Error al aceptar oferta', 'Error', 'error');
    }
  };

  const handleRejectOffer = async (offerId) => {
    try {
      const json = await api.rejectOffer(offerId);
      if (json.success) {
        showAlert('Oferta rechazada', 'Info', 'info');
        loadOffers();
      }
    } catch (err) {
      showAlert('Error al rechazar oferta', 'Error', 'error');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const formatOfferStatus = (status) => {
    const statuses = {
      pending: '⏳ Pendiente',
      accepted: '✅ Aceptada',
      rejected: '❌ Rechazada',
      countered: '🔄 Contraoferta',
      expired: '⏰ Expirada'
    };
    return statuses[status] || status;
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <SellerInfo>
            <div className="avatar">
              {sellerInfo?.name ? sellerInfo.name.charAt(0).toUpperCase() : '?'}
            </div>
            <div className="info">
              <div className="name" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {sellerInfo?.name || t('seller')}
                {sellerInfo && <VerificationBadge user={sellerInfo} />}
              </div>
              <div className="ad-title">{adTitle}</div>
            </div>
          </SellerInfo>
          <CloseButton onClick={onClose}>
            <i className="fas fa-times"></i>
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* Offers Section */}
          {offers.length > 0 && (
            <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>
                Ofertas ({offers.length})
              </h4>
              {offers.map(offer => (
                <OfferCard key={offer.public_id} status={offer.status}>
                  <div>
                    <strong>Rp {parseFloat(offer.amount).toLocaleString('id-ID')}</strong>
                    <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                      {formatOfferStatus(offer.status)}
                    </span>
                    {offer.message && <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>{offer.message}</p>}
                  </div>
                  {!offer.is_mine && offer.status === 'pending' && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleAcceptOffer(offer.public_id)}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Aceptar
                      </button>
                      <button
                        onClick={() => handleRejectOffer(offer.public_id)}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Rechazar
                      </button>
                    </div>
                  )}
                </OfferCard>
              ))}
            </div>
          )}

          <MessagesContainer>
            {loading ? (
              <EmptyState>
                <i className="fas fa-spinner fa-spin"></i>
                <p>{t('loading_messages') || 'Cargando mensajes...'}</p>
              </EmptyState>
            ) : messages.length === 0 ? (
              <EmptyState>
                <i className="fas fa-comments"></i>
                <p>{t('no_messages_yet') || 'No hay mensajes aún'}</p>
                <small>{t('start_conversation') || '¡Inicia la conversación!'}</small>
              </EmptyState>
            ) : (
              messages.map((msg, index) => {
                // Handle both created_at and createdAt field names
                const timestamp = msg.created_at || msg.createdAt;
                const messageTime = timestamp 
                  ? timeAgo(timestamp, 'es')
                  : '';

                return (
                  <Message key={msg.id || index} fromMe={msg.from_me}>
                    <div className="message-content">
                      {msg.message}
                    </div>
                    <div className="message-time">
                      {messageTime}
                    </div>
                  </Message>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </MessagesContainer>
        </ModalBody>

        {/* Quick Replies */}
        {showQuickReplies && (
          <QuickReplyContainer>
            {QUICK_REPLIES.map(reply => (
              <QuickReplyButton
                key={reply.id}
                onClick={() => handleQuickReply(reply.text)}
              >
                {reply.text}
              </QuickReplyButton>
            ))}
          </QuickReplyContainer>
        )}

        <ModalFooter>
          <div style={{ display: 'flex', width: '100%', gap: '8px', alignItems: 'center' }}>
            {/* Quick Reply Toggle */}
            <button
              onClick={() => setShowQuickReplies(!showQuickReplies)}
              style={{
                padding: '10px',
                backgroundColor: showQuickReplies ? '#667eea' : '#f0f0f0',
                color: showQuickReplies ? 'white' : '#666',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
              title="Respuestas rápidas"
            >
              <i className="fas fa-comment-dots"></i>
            </button>

            {/* Offer Button - Now available for ALL ads */}
            <OfferButton onClick={() => setShowOfferModal(true)} title={t('make_offer') || 'Hacer una oferta'}>
              <i className="fas fa-hand-holding-usd"></i>
            </OfferButton>

            <form onSubmit={handleSendMessage} style={{ display: 'flex', flex: 1, gap: '8px' }}>
              <MessageInput
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('type_message') || 'Escribe un mensaje...'}
                disabled={sending}
              />
              <SendButton type="submit" disabled={!newMessage.trim() || sending}>
                {sending ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <i className="fas fa-paper-plane"></i>
                )}
              </SendButton>
            </form>
          </div>
        </ModalFooter>

        {/* Offer Modal */}
        {showOfferModal && (
          <OfferModal onClick={() => setShowOfferModal(false)}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '400px',
                width: '90%'
              }}
            >
              <h3 style={{ marginTop: 0 }}>{t('make_offer') || 'Hacer una Oferta'}</h3>
              {adPrice && (
                <>
                  <p style={{ color: '#666', fontSize: '14px', margin: '0 0 4px 0' }}>
                    {t('price') || 'Precio actual'}: Rp {parseFloat(adPrice).toLocaleString('id-ID')}
                  </p>
                  <p style={{ color: '#28a745', fontSize: '13px', fontWeight: '500', margin: '0 0 16px 0' }}>
                    Oferta mínima: Rp {(adPrice * 0.75).toLocaleString('id-ID')} (75%)
                  </p>
                </>
              )}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  {t('offer_amount') || 'Monto de la oferta'} (Rp)
                </label>
                <input
                  type="number"
                  value={offerAmount}
                  onChange={(e) => setOfferAmount(e.target.value)}
                  placeholder={adPrice ? `Mín: ${(adPrice * 0.75).toLocaleString('id-ID')}` : 'Ej: 500000'}
                  min={adPrice ? adPrice * 0.75 : 0}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  {t('offer_message') || 'Mensaje (opcional)'}
                </label>
                <textarea
                  value={offerMessage}
                  onChange={(e) => setOfferMessage(e.target.value)}
                  placeholder={t('type_message') || 'Escribe un mensaje...'}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowOfferModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f0f0f0',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  {t('cancel') || 'Cancelar'}
                </button>
                <button
                  onClick={handleSendOffer}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {t('send_offer') || 'Enviar Oferta'}
                </button>
              </div>
            </div>
          </OfferModal>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ChatModal;
